import React from 'react';
import { X, Bell, AlertTriangle, CheckCircle, FileText, Share2, ServerCrash, Clock } from 'lucide-react';

interface MobileNotificationsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: any[];
  onMarkAllRead: () => void;
  onOpenDemand: (demandId: string) => void;
}

export const MobileNotificationsDrawer: React.FC<MobileNotificationsDrawerProps> = ({
  isOpen,
  onClose,
  notifications,
  onMarkAllRead,
  onOpenDemand
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm z-50 fade-backdrop md:hidden"
        onClick={onClose}
      />
      <div 
        className="fixed bottom-0 inset-x-0 bg-white dark:bg-slate-950 rounded-t-3xl shadow-2xl z-50 flex flex-col md:hidden transition-transform duration-300"
        style={{ maxHeight: '85vh', animation: 'slideUp 0.3s ease-out' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full" />
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-lg font-bold flex items-center gap-2 text-slate-800 dark:text-slate-100">
            <Bell className="w-5 h-5 text-blue-500" />
            Alertas
          </h2>
          <div className="flex items-center gap-3">
            <button onClick={onMarkAllRead} className="text-xs font-semibold text-blue-600 dark:text-blue-500">
              Ler todas
            </button>
            <button onClick={onClose} className="p-1.5 bg-slate-100 dark:bg-slate-900 rounded-full text-slate-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-slate-400">
              <CheckCircle className="w-10 h-10 mb-3 opacity-50" />
              <p className="text-sm font-semibold">Tudo limpo por aqui</p>
              <p className="text-xs opacity-70">Nenhum alerta novo.</p>
            </div>
          ) : (
            notifications.map((n) => {
              // Custom icons and colors based on notification type
              let Icon = Bell;
              let colorClass = 'text-blue-500';
              let bgClass = n.read ? 'bg-slate-50 dark:bg-slate-900 opacity-70' : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50';

              if (n.type === 'NEW_DEMAND') { Icon = FileText; colorClass = 'text-blue-500'; }
              if (n.type === 'URGENT') { Icon = AlertTriangle; colorClass = 'text-red-500'; bgClass = n.read ? bgClass : 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50'; }
              if (n.type === 'APPROVED') { Icon = CheckCircle; colorClass = 'text-emerald-500'; }
              if (n.type === 'REJECTED') { Icon = X; colorClass = 'text-red-500'; }
              if (n.type === 'ATTACHMENT') { Icon = Share2; colorClass = 'text-violet-500'; }
              if (n.type === 'WHATSAPP_FAIL' || n.type === 'IA_FAIL') { Icon = ServerCrash; colorClass = 'text-amber-500'; }

              return (
                <div 
                  key={n.id}
                  onClick={() => {
                    if (n.demandId) {
                      onOpenDemand(n.demandId);
                      onClose();
                    }
                  }}
                  className={`p-3.5 rounded-2xl flex gap-3 cursor-pointer transition-all active:scale-[0.98] ${bgClass}`}
                >
                  <div className="shrink-0 mt-0.5">
                    <Icon className={`w-5 h-5 ${colorClass}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <p className={`text-sm font-bold truncate ${n.read ? 'text-slate-600 dark:text-slate-400' : 'text-slate-900 dark:text-slate-100'}`}>
                        {n.title}
                      </p>
                      <span className="text-[10px] text-slate-400 font-mono flex items-center gap-1 shrink-0 whitespace-nowrap mt-0.5">
                        <Clock className="w-3 h-3" />
                        {new Date(n.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
                      {n.content}
                    </p>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </>
  );
};
